/**
 * AudioManager - Procedural Sound Generation for Magic Sandbox
 * Uses Web Audio API to generate all sounds programmatically (no external files)
 */

import { ElementType } from '../types';

export interface AudioSettings {
  enabled: boolean;
  masterVolume: number;
  sfxVolume: number;
  ambientVolume: number;
}

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  enabled: true,
  masterVolume: 70,
  sfxVolume: 80,
  ambientVolume: 30,
};

export type SpellType = 
  | 'TORNADO' 
  | 'LIGHTNING' 
  | 'METEOR' 
  | 'BLACKHOLE' 
  | 'RAINCLOUD' 
  | 'BOMB' 
  | 'EXPLOSION'
  | 'FREEZE_RAY'
  | 'EARTHQUAKE'
  | 'TSUNAMI';

export type MaterialCategory = 
  | 'SOLID'
  | 'LIQUID'
  | 'ORGANIC'
  | 'GAS'
  | 'SPECIAL';

class AudioManager {
  private ctx: AudioContext | null = null;
  private settings: AudioSettings = { ...DEFAULT_AUDIO_SETTINGS };
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private ambientOscillator: OscillatorNode | null = null;
  private isInitialized = false;
  private lastPlayTime: Map<string, number> = new Map();
  private readonly MIN_PLAY_INTERVAL = 50;

  initialize(): boolean {
    if (this.isInitialized) return true;
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        console.warn('Web Audio API not supported');
        return false;
      }
      this.ctx = new AudioContextClass();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.settings.masterVolume / 100;
      this.masterGain.connect(this.ctx.destination);
      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this.settings.sfxVolume / 100;
      this.sfxGain.connect(this.masterGain);
      this.ambientGain = this.ctx.createGain();
      this.ambientGain.gain.value = this.settings.ambientVolume / 100;
      this.ambientGain.connect(this.masterGain);
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize audio:', error);
      return false;
    }
  }

  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  updateSettings(newSettings: Partial<AudioSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    if (!this.isInitialized) return;
    if (this.masterGain) {
      this.masterGain.gain.value = this.settings.enabled ? this.settings.masterVolume / 100 : 0;
    }
    if (this.sfxGain) {
      this.sfxGain.gain.value = this.settings.sfxVolume / 100;
    }
    if (this.ambientGain) {
      this.ambientGain.gain.value = this.settings.ambientVolume / 100;
    }
    if (this.settings.enabled && !this.ambientOscillator) {
      this.startAmbientLoop();
    } else if (!this.settings.enabled && this.ambientOscillator) {
      this.stopAmbientLoop();
    }
  }

  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  toggleMute(): boolean {
    this.updateSettings({ enabled: !this.settings.enabled });
    return this.settings.enabled;
  }

  setMasterVolume(volume: number): void {
    this.updateSettings({ masterVolume: Math.max(0, Math.min(100, volume)) });
  }

  setSfxVolume(volume: number): void {
    this.updateSettings({ sfxVolume: Math.max(0, Math.min(100, volume)) });
  }

  setAmbientVolume(volume: number): void {
    this.updateSettings({ ambientVolume: Math.max(0, Math.min(100, volume)) });
  }

  private canPlay(soundType: string): boolean {
    const now = Date.now();
    const lastTime = this.lastPlayTime.get(soundType) || 0;
    if (now - lastTime < this.MIN_PLAY_INTERVAL) return false;
    this.lastPlayTime.set(soundType, now);
    return true;
  }

  private getMaterialCategory(elementType: ElementType): MaterialCategory {
    switch (elementType) {
      case ElementType.SAND:
      case ElementType.STONE:
      case ElementType.GLASS:
      case ElementType.METAL:
      case ElementType.DIRT:
      case ElementType.SALT:
        return 'SOLID';
      case ElementType.WATER:
      case ElementType.OIL:
      case ElementType.ACID:
      case ElementType.LAVA:
      case ElementType.MUD:
      case ElementType.SLIME:
      case ElementType.MAGMA:
        return 'LIQUID';
      case ElementType.WOOD:
      case ElementType.PLANT:
      case ElementType.FLOWER:
      case ElementType.WAX:
      case ElementType.FUSE:
      case ElementType.COAL:
        return 'ORGANIC';
      case ElementType.FIRE:
      case ElementType.SMOKE:
      case ElementType.STEAM:
      case ElementType.EMBER:
      case ElementType.ELECTRIC_SPARK:
        return 'GAS';
      default:
        return 'SPECIAL';
    }
  }

  private createNoiseBuffer(duration: number): AudioBuffer | null {
    if (!this.ctx) return null;
    const sampleRate = this.ctx.sampleRate;
    const buffer = this.ctx.createBuffer(1, Math.ceil(duration * sampleRate), sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }

  private playFilteredNoise(
    duration: number,
    filterFreq: number,
    filterType: BiquadFilterType = 'lowpass',
    volume: number = 1,
    attack: number = 0.01,
    decay: number = 0.1,
    pan: number = 0
  ): void {
    if (!this.ctx || !this.sfxGain) return;
    const noiseBuffer = this.createNoiseBuffer(duration);
    if (!noiseBuffer) return;
    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = filterFreq;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(volume, this.ctx.currentTime + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + attack + decay);
    const panner = this.ctx.createStereoPanner();
    panner.pan.value = pan;
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(panner);
    panner.connect(this.sfxGain);
    noise.start();
    noise.stop(this.ctx.currentTime + duration);
  }

  private playThud(volume: number = 1, pan: number = 0): void {
    if (!this.ctx || !this.sfxGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(40, this.ctx.currentTime + 0.15);
    filter.type = 'lowpass';
    filter.frequency.value = 400;
    gain.gain.setValueAtTime(volume * 0.8, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.15);
    const panner = this.ctx.createStereoPanner();
    panner.pan.value = pan;
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(panner);
    panner.connect(this.sfxGain);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.15);
  }

  private playSplash(volume: number = 1, pan: number = 0): void {
    this.playFilteredNoise(0.3, 800, 'lowpass', volume * 0.6, 0.01, 0.2, pan);
  }

  private playRustle(volume: number = 1, pan: number = 0): void {
    this.playFilteredNoise(0.15, 2000, 'bandpass', volume * 0.4, 0.005, 0.1, pan);
  }

  private playCrumble(volume: number = 1, pan: number = 0): void {
    this.playFilteredNoise(0.4, 600, 'lowpass', volume * 0.7, 0.01, 0.3, pan);
  }

  private playShatter(volume: number = 1, pan: number = 0): void {
    if (!this.ctx || !this.sfxGain) return;
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        if (!this.ctx || !this.sfxGain) return;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(2000 + Math.random() * 1500, this.ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(100, this.ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(volume * 0.15, this.ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.1);
        const panner = this.ctx.createStereoPanner();
        panner.pan.value = pan + (Math.random() - 0.5) * 0.5;
        osc.connect(gain);
        gain.connect(panner);
        panner.connect(this.sfxGain);
        osc.start();
        osc.stop(this.ctx.currentTime + 0.1);
      }, i * 15);
    }
  }

  private playExplosionSound(volume: number = 1, pan: number = 0): void {
    if (!this.ctx || !this.sfxGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(100, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(20, this.ctx.currentTime + 0.5);
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, this.ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(100, this.ctx.currentTime + 0.5);
    gain.gain.setValueAtTime(volume, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.5);
    const panner = this.ctx.createStereoPanner();
    panner.pan.value = pan;
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(panner);
    panner.connect(this.sfxGain);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.5);
    this.playFilteredNoise(0.4, 400, 'lowpass', volume * 0.8, 0.01, 0.35, pan);
  }

  private playChargingHum(volume: number = 1, pan: number = 0): void {
    if (!this.ctx || !this.sfxGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, this.ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(600, this.ctx.currentTime + 0.3);
    filter.type = 'lowpass';
    filter.frequency.value = 1000;
    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(volume * 0.4, this.ctx.currentTime + 0.1);
    gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.3);
    const panner = this.ctx.createStereoPanner();
    panner.pan.value = pan;
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(panner);
    panner.connect(this.sfxGain);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.3);
  }

  private playReleaseBurst(volume: number = 1, pan: number = 0): void {
    if (!this.ctx || !this.sfxGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(200, this.ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(volume * 0.6, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.2);
    const panner = this.ctx.createStereoPanner();
    panner.pan.value = pan;
    osc.connect(gain);
    gain.connect(panner);
    panner.connect(this.sfxGain);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.2);
  }

  playPlaceSound(elementType: ElementType, pan: number = 0): void {
    if (!this.settings.enabled || !this.canPlay('place')) return;
    if (!this.initialize()) return;
    this.resume();
    const category = this.getMaterialCategory(elementType);
    switch (category) {
      case 'SOLID':
        this.playThud(0.5, pan);
        break;
      case 'LIQUID':
        this.playSplash(0.4, pan);
        break;
      case 'ORGANIC':
        this.playRustle(0.3, pan);
        break;
      case 'GAS':
        this.playFilteredNoise(0.1, 1000, 'highpass', 0.15, 0.01, 0.05, pan);
        break;
      case 'SPECIAL':
        if (elementType === ElementType.ICE) {
          this.playShatter(0.2, pan);
        } else {
          this.playThud(0.3, pan);
        }
        break;
    }
  }

  playDestroySound(elementType: ElementType, pan: number = 0): void {
    if (!this.settings.enabled || !this.canPlay('destroy')) return;
    if (!this.initialize()) return;
    this.resume();
    const category = this.getMaterialCategory(elementType);
    switch (category) {
      case 'SOLID':
        this.playCrumble(0.6, pan);
        break;
      case 'LIQUID':
        this.playSplash(0.3, pan);
        break;
      case 'ORGANIC':
        this.playRustle(0.4, pan);
        break;
      case 'GAS':
        break;
      case 'SPECIAL':
        if (elementType === ElementType.ICE || elementType === ElementType.GLASS) {
          this.playShatter(0.7, pan);
        } else {
          this.playCrumble(0.5, pan);
        }
        break;
    }
  }

  playSpellSound(spellType: SpellType, pan: number = 0): void {
    if (!this.settings.enabled) return;
    if (!this.initialize()) return;
    this.resume();
    switch (spellType) {
      case 'TORNADO':
        if (this.canPlay('spell_tornado')) {
          this.playChargingHum(0.5, pan);
          setTimeout(() => this.playReleaseBurst(0.4, pan), 200);
        }
        break;
      case 'LIGHTNING':
        if (this.canPlay('spell_lightning')) {
          this.playFilteredNoise(0.2, 3000, 'highpass', 0.8, 0.001, 0.15, pan);
          setTimeout(() => {
            this.playFilteredNoise(0.15, 2000, 'bandpass', 0.5, 0.001, 0.1, pan);
          }, 50);
        }
        break;
      case 'METEOR':
        if (this.canPlay('spell_meteor')) {
          this.playFilteredNoise(1.0, 200, 'lowpass', 0.6, 0.5, 0.8, pan);
        }
        break;
      case 'BLACKHOLE':
        if (this.canPlay('spell_blackhole') && this.ctx && this.sfxGain) {
          const osc = this.ctx.createOscillator();
          const gain = this.ctx.createGain();
          const filter = this.ctx.createBiquadFilter();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(80, this.ctx.currentTime);
          osc.frequency.linearRampToValueAtTime(40, this.ctx.currentTime + 0.5);
          filter.type = 'lowpass';
          filter.frequency.value = 300;
          gain.gain.setValueAtTime(0, this.ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0.5, this.ctx.currentTime + 0.2);
          gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.5);
          const panner = this.ctx.createStereoPanner();
          panner.pan.value = pan;
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(panner);
          panner.connect(this.sfxGain);
          osc.start();
          osc.stop(this.ctx.currentTime + 0.5);
        }
        break;
      case 'RAINCLOUD':
        if (this.canPlay('spell_raincloud')) {
          this.playFilteredNoise(0.5, 600, 'lowpass', 0.4, 0.2, 0.4, pan);
        }
        break;
      case 'BOMB':
        if (this.canPlay('spell_bomb')) {
          this.playThud(0.5, pan);
        }
        break;
      case 'EXPLOSION':
        if (this.canPlay('spell_explosion')) {
          this.playExplosionSound(1.0, pan);
        }
        break;
      case 'FREEZE_RAY':
        if (this.canPlay('spell_freeze')) {
          this.playFilteredNoise(0.3, 2000, 'highpass', 0.5, 0.01, 0.2, pan);
        }
        break;
      case 'EARTHQUAKE':
        if (this.canPlay('spell_earthquake')) {
          this.playThud(0.8, pan);
          setTimeout(() => this.playThud(0.6, pan), 100);
          setTimeout(() => this.playThud(0.4, pan), 200);
        }
        break;
      case 'TSUNAMI':
        if (this.canPlay('spell_tsunami')) {
          this.playFilteredNoise(1.5, 400, 'lowpass', 0.7, 0.3, 1.0, pan);
        }
        break;
    }
  }

  playExplosion(intensity: number = 1, pan: number = 0): void {
    if (!this.settings.enabled || !this.canPlay('explosion')) return;
    if (!this.initialize()) return;
    this.resume();
    this.playExplosionSound(Math.min(1, intensity), pan);
  }

  startAmbientLoop(): void {
    if (!this.ctx || !this.ambientGain || this.ambientOscillator) return;
    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    osc1.type = 'sine';
    osc1.frequency.value = 60;
    osc2.type = 'triangle';
    osc2.frequency.value = 62;
    filter.type = 'lowpass';
    filter.frequency.value = 200;
    gain.gain.value = 0.05;
    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.ambientGain);
    osc1.start();
    osc2.start();
    this.ambientOscillator = osc1;
    (this.ambientOscillator as any)._osc2 = osc2;
  }

  stopAmbientLoop(): void {
    if (this.ambientOscillator) {
      try {
        this.ambientOscillator.stop();
        const osc2 = (this.ambientOscillator as any)._osc2;
        if (osc2) osc2.stop();
      } catch (e) {}
      this.ambientOscillator = null;
    }
  }

  dispose(): void {
    this.stopAmbientLoop();
    if (this.ctx && this.ctx.state !== 'closed') {
      this.ctx.close();
    }
    this.ctx = null;
    this.masterGain = null;
    this.sfxGain = null;
    this.ambientGain = null;
    this.isInitialized = false;
  }
}

export const audioManager = new AudioManager();
export default audioManager;
